import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2, X, TicketCheck, LogIn, PlusCircle, MessageSquare } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toaster';

export function LoginPage() {
  const { user, signIn, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  if (!loading && user) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signIn(email, password);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Authentication failed';
      toast({ title: 'Sign in failed', description: message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-ink text-cream">
      <div className="pointer-events-none absolute inset-0 opacity-30">
        <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-gold-500 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 h-[28rem] w-[28rem] rounded-full bg-gold-700 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl flex-col items-center justify-center gap-6 px-6 py-12 lg:flex-row lg:gap-24">
        {/* Logo section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative flex flex-col items-center"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="absolute -inset-4 rounded-2xl bg-gradient-to-r from-gold-500/40 via-gold-200/40 to-gold-500/40 blur-2xl"
          />
          <motion.img
            src="/chhabighar.png"
            alt="CHHABIGHAR"
            className="relative h-36 w-36 rounded-2xl object-contain shadow-2xl ring-2 ring-gold-400/40 sm:h-56 sm:w-56 lg:h-72 lg:w-72"
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-6 text-center"
          >
            <h1 className="font-serif text-3xl tracking-wider text-shimmer">
              CHHABIGHAR
            </h1>
            {/* Changed from "Retail · ERP · POS · CRM" */}
            <p className="mt-1 text-sm text-cream/60">
              Powered by NexaRise
            </p>
          </motion.div>
        </motion.div>

        {/* Sign in form — sign up removed */}
        <motion.form
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          onSubmit={onSubmit}
          className="w-full max-w-sm rounded-xl border border-gold-500/30 bg-ink/70 p-6 shadow-2xl backdrop-blur sm:p-8"
        >
          <h2 className="font-serif text-2xl text-cream">Welcome back</h2>
          <p className="mt-1 text-sm text-cream/60">Sign in to manage your business.</p>

          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-cream/80">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-gold-500/30 bg-ink text-cream placeholder:text-cream/30"
                placeholder="owner@chhabighar.local"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-cream/80">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="border-gold-500/30 bg-ink pr-10 text-cream placeholder:text-cream/30"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-cream/60 transition-colors hover:bg-gold-500/10 hover:text-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/50"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
                </button>
              </div>
            </div>
          </div>

          <Button type="submit" variant="gold" size="lg" className="mt-6 w-full" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign in
          </Button>

          {/* "Connect Support" — opens info modal, no auth required */}
          <button
            type="button"
            onClick={() => setSupportOpen(true)}
            className="mt-4 block w-full text-center text-xs text-cream/50 hover:text-gold-400 transition-colors underline underline-offset-2"
          >
            Connect Support
          </button>
        </motion.form>
      </div>

      {/* ── Support Info Modal (no auth required) ── */}
      {supportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSupportOpen(false)} />
          <div className="relative z-10 w-full max-w-md rounded-xl border border-gold-500/30 bg-background p-6 shadow-2xl">
            {/* Close */}
            <button
              onClick={() => setSupportOpen(false)}
              className="absolute right-4 top-4 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Header */}
            <div className="flex items-center gap-2 mb-1">
              <TicketCheck className="h-5 w-5 text-gold-600" />
              <h2 className="font-serif text-lg font-semibold">Connect Support</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Use your login credentials to login to the support system so that you can create a ticket for whatever issues you are facing.
            </p>

            {/* Steps */}
            <div className="space-y-4">
              {[
                { icon: LogIn,        step: 1, title: 'Log in to NexaRise Support', desc: 'Visit support.nexarise.co/chat and sign in using the same email and password you use here.' },
                { icon: PlusCircle,   step: 2, title: 'Create a new ticket',        desc: 'Click "New Ticket", describe your issue in detail, and select the relevant category.' },
                { icon: MessageSquare,step: 3, title: 'Submit & communicate',       desc: 'Submit the ticket and reply to the support team directly from the ticket page.' },
                { icon: Eye,          step: 4, title: 'Track progress',             desc: 'Monitor your ticket status (Open, In Progress, Resolved) from your dashboard.' },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex gap-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gold-500/15 text-gold-600 text-xs font-bold">
                    {step}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <a
              href="https://support.nicecare.co/chat"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-gold-500 px-4 py-2.5 text-sm font-medium text-ink hover:bg-gold-400 transition-colors"
            >
              Open Support Portal
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
